
import { useState, useEffect, useCallback } from 'react';
import { Poll } from '@/types/poll';
import { fetchPollById, fetchPollByNumber, fetchPollVotesDistribution, fetchRelatedInsightCards } from '@/utils/polls/pollDetailsUtils';
import { useUserVotes } from './useUserVotes';
import { useVotePermissions } from './useVotePermissions';
import { supabase } from '@/integrations/supabase/client';
import { debugVoteFlow } from '@/utils/debug';
import { usePollVotesRealtime } from './usePollVotesRealtime';

interface UsePollDetailsProps {
  pollId?: string;
  pollNumber?: string;
}

export const usePollDetails = ({ pollId, pollNumber }: UsePollDetailsProps) => {
  // Use array-based state management like the working widget
  const [polls, setPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [votesDistribution, setVotesDistribution] = useState<any[] | null>(null);
  const [relatedCards, setRelatedCards] = useState<any[]>([]);
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(null);
  const [shareableUrl, setShareableUrl] = useState<string>('');
  
  const { userVotes, setUserVotes } = useUserVotes();
  const { canVote, cannotVoteReason } = useVotePermissions();
  
  // Use the same real-time system as the working widget - this will now properly subscribe
  const { markRecentVote } = usePollVotesRealtime(polls, setPolls, userVotes, setUserVotes);
  
  // Get the single poll from the polls array (same pattern as widget)
  const poll = polls.length > 0 ? polls[0] : null;
  
  debugVoteFlow('POLL_DETAILS_STATE', { 
    currentPoll: poll?.id,
    pollId,
    pollNumber,
    pollsLength: polls.length,
    realtimeActive: polls.length > 0
  });
  
  // Check if the user has already voted on this poll
  const hasVoted = poll ? userVotes[poll.id] !== undefined : false;
  const userVoteOptionId = poll && hasVoted ? userVotes[poll.id] : null;
  
  // Optimistic vote update function that works with array-based state
  const updatePollOptimistically = useCallback((pollId: string, optionId: number) => {
    debugVoteFlow('POLL_DETAILS_OPTIMISTIC_UPDATE', { pollId, optionId });
    
    setPolls(currentPolls => currentPolls.map(poll => {
      if (poll.id === pollId) {
        const newTotalVotes = poll.total_votes + 1;
        
        const updatedOptions = poll.options.map(option => {
          if (option.id === optionId) {
            const newVotes = option.votes + 1;
            return {
              ...option,
              votes: newVotes,
              percentage: Math.round((newVotes / newTotalVotes) * 100)
            };
          }
          // Recalculate percentages for all options based on new total
          return {
            ...option,
            percentage: Math.round((option.votes / newTotalVotes) * 100)
          };
        });

        const updatedPoll = {
          ...poll,
          options: updatedOptions,
          total_votes: newTotalVotes,
          questions: {
            ...poll.questions,
            options: updatedOptions,
            total_votes: newTotalVotes
          }
        };

        debugVoteFlow('POLL_DETAILS_OPTIMISTIC_UPDATED', {
          pollId: poll.id,
          optionId,
          newVoteCount: updatedOptions.find(o => o.id === optionId)?.votes,
          newTotal: updatedPoll.total_votes
        });

        return updatedPoll;
      }
      return poll;
    }));

    // Update user votes
    setUserVotes(prevVotes => ({
      ...prevVotes,
      [pollId]: optionId
    }));

    // Mark as recent vote to prevent double counting in real-time
    markRecentVote(pollId);
  }, [setPolls, setUserVotes, markRecentVote]);
  
  // Function to fetch poll data
  const fetchPollData = useCallback(async () => {
    if (!pollId && !pollNumber) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      debugVoteFlow('POLL_DETAILS_FETCH_START', { pollId, pollNumber });
      
      let pollData: Poll | null = null;

      // Try to fetch by poll number first if provided
      if (pollNumber) {
        pollData = await fetchPollByNumber(pollNumber);
      }
      
      // If not found by number or no number provided, try by ID
      if (!pollData && pollId) {
        pollData = await fetchPollById(pollId);
      }

      // If we found a poll, set it
      if (pollData) {
        debugVoteFlow('POLL_DETAILS_FETCH_SUCCESS', { pollId: pollData.id });
        
        // Update poll options counts using existing function
        try {
          await supabase.rpc('update_poll_options_counts', { poll_id: pollData.id });
        } catch (reconcileError) {
          console.warn('Failed to update poll options counts:', reconcileError);
          // Continue even if this fails
        }
        
        // Set as a single-item array for real-time updates (same as widget pattern)
        // This is crucial - the real-time subscription needs the poll to be in the array
        setPolls([pollData]);
        
        debugVoteFlow('POLL_DETAILS_REALTIME_READY', { 
          pollId: pollData.id,
          pollsArrayLength: 1
        });
        
        // Use consistent poll routing - always use /poll/:id to avoid conflicts
        const pollPath = `/poll/${pollData.id}`;
        setCanonicalUrl(pollPath);
        
        // Set the full absolute URL for sharing
        const origin = window.location.origin;
        setShareableUrl(`${origin}${pollPath}`);
        
        // Fetch vote distribution for charts
        const votesData = await fetchPollVotesDistribution(pollData.id);
        setVotesDistribution(votesData);
        
        // Fetch related cards
        const cards = await fetchRelatedInsightCards(pollData.id);
        setRelatedCards(cards);
      } else {
        debugVoteFlow('POLL_DETAILS_FETCH_NOT_FOUND', { pollId, pollNumber });
        setError(new Error("Poll not found"));
      }
    } catch (fetchError) {
      debugVoteFlow('POLL_DETAILS_FETCH_ERROR', { error: fetchError });
      console.error('Error fetching poll details:', fetchError);
      setError(fetchError as Error);
    } finally {
      setIsLoading(false);
    }
  }, [pollId, pollNumber]);
  
  // Fetch poll data on mount and when pollId changes
  useEffect(() => {
    fetchPollData();
  }, [fetchPollData]);
  
  return {
    poll,
    isLoading,
    error,
    hasVoted,
    userVoteOptionId,
    canVote: canVote(),
    cannotVoteReason: cannotVoteReason(),
    refresh: fetchPollData,
    votesDistribution,
    relatedCards,
    canonicalUrl,
    shareableUrl,
    updatePollOptimistically,
    // Add poll management state for universal component
    polls,
    setPolls,
    userVotes,
    setUserVotes,
    markRecentVote
  };
};
